/**
 * Split a string into a sequence of grapheme-aware tokens. We need this so
 * `replaceEmojisWithReactions` can swap individual emojis without breaking
 * complex sequences (ZWJ families, skin-tone modifiers, regional indicators).
 *
 * Implementation strategy:
 *   - Prefer `Intl.Segmenter` when available (modern browsers + Node 16+)
 *     so ZWJ, flags and skin-tone sequences stay intact.
 *   - Fall back to a Unicode regex that matches common emoji clusters.
 *
 * The function is shared between server and client renderers so the output
 * (and therefore the React key sequence) is deterministic.
 */
type Segment = { text: string; isEmoji: boolean };

const EMOJI_REGEX =
  /(?:\p{Regional_Indicator}{2})|(?:[\p{Extended_Pictographic}\p{Emoji_Presentation}](?:\p{Emoji_Modifier})?(?:\uFE0F)?(?:\u200D[\p{Extended_Pictographic}\p{Emoji_Presentation}](?:\p{Emoji_Modifier})?(?:\uFE0F)?)*)/gu;
const EMOJI_DETECT =
  /(?:\p{Regional_Indicator}{2})|(?:[\p{Extended_Pictographic}\p{Emoji_Presentation}]\p{Emoji_Modifier}?\uFE0F?)/u;

/** ¿El texto es un emoji (glifo) y no una palabra? */
export function isEmojiGlyph(text: string | null | undefined): boolean {
  return !!text && EMOJI_DETECT.test(text);
}

export function splitTextForReactions(input: string): Segment[] {
  if (!input) return [];
  const segmented = splitWithIntlSegmenter(input);
  if (segmented) return segmented;

  const out: Segment[] = [];
  let lastIndex = 0;
  // Reset state for the global regex.
  EMOJI_REGEX.lastIndex = 0;
  for (const match of input.matchAll(EMOJI_REGEX)) {
    const index = match.index ?? 0;
    if (index > lastIndex) {
      out.push({ text: input.slice(lastIndex, index), isEmoji: false });
    }
    out.push({ text: match[0], isEmoji: true });
    lastIndex = index + match[0].length;
  }
  if (lastIndex < input.length) {
    out.push({ text: input.slice(lastIndex), isEmoji: false });
  }
  return out;
}

function splitWithIntlSegmenter(input: string): Segment[] | null {
  const Segmenter = Intl.Segmenter;
  if (!Segmenter) return null;
  const segmenter = new Segmenter(undefined, { granularity: "grapheme" });
  const out: Segment[] = [];
  for (const part of segmenter.segment(input)) {
    const text = part.segment;
    const isEmoji = EMOJI_DETECT.test(text);
    const last = out[out.length - 1];
    if (!isEmoji && last && !last.isEmoji) {
      last.text += text;
    } else {
      out.push({ text, isEmoji });
    }
  }
  return out;
}
